<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { ElMessageBox } from "element-plus";
import dayjs from "dayjs";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import ApiApplicationDialog from "@/components/security/ApiApplicationDialog.vue";
import ApiApplicationSecretDialog from "@/components/security/ApiApplicationSecretDialog.vue";
import Notification from "@/utils/plugins/NotificationService";
import ApiApplicationService from "@/api/services/apiApplication.service";

const page = ref(1);
const itemsPerPage = ref(10);
const router = useRouter();
const filters = ref({ search: "", status: "" });

const rawApplications = ref([]);
const showApplicationDialog = ref(false);
const showSecretDialog = ref(false);
const selectedApplication = ref(null);
const generatedSecret = ref("");
const secretDialogTitle = ref("Aplicación creada con éxito");

const tones = ["#b88761", "#96634d", "#a69964", "#628db2", "#9d705b", "#bb9564", "#6d95b3", "#b77867", "#8d7658", "#ad668b", "#9871ad", "#4f4f4f"];
const toneFor = (id) => tones[id % tones.length];
const initialsOf = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const normalize = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const loadApplications = () => {
  ApiApplicationService.getApiApplications().then((res) => {
    if (!res) return;
    rawApplications.value = res.data ?? [];
  });
};

const applications = computed(() => rawApplications.value.map((app) => ({
  id: app.id,
  initials: initialsOf(app.name),
  name: app.name,
  description: app.description ?? "",
  prefix: app.prefix,
  status: app.revokedAt ? "revoked" : (app.currentlyValid ? "active" : "expired"),
  createdAt: dayjs(app.createdAt),
  expiresAt: app.expiresAt ? dayjs(app.expiresAt) : null,
  lastUsedAt: app.lastUsedAt ? dayjs(app.lastUsedAt) : null,
  requestLimit: app.requestLimit ?? null,
  requestLimitIntervalMinutes: app.requestLimitIntervalMinutes ?? null,
  tone: toneFor(app.id),
})));

const statusLabel = { active: "Activa", expired: "Caducada", revoked: "Revocada" };
const lastUsedLabel = (app) => (app.lastUsedAt ? app.lastUsedAt.format("DD/MM/YYYY HH:mm") : "Sin uso");
const expiresLabel = (app) => (app.expiresAt ? app.expiresAt.format("DD/MM/YYYY") : "Sin caducidad");
const requestLimitLabel = (app) => (app.requestLimit ? `${app.requestLimit} / ${app.requestLimitIntervalMinutes} min` : "Por defecto");

const filteredApplications = computed(() => applications.value.filter((app) =>
  (!filters.value.search || normalize(`${app.name} ${app.description}`).includes(normalize(filters.value.search))) &&
  (!filters.value.status || app.status === filters.value.status)
));
const listedApplications = computed(() => filteredApplications.value.slice((page.value - 1) * itemsPerPage.value, page.value * itemsPerPage.value));
const search = () => { page.value = 1; };

const editingApplicationId = ref(null);

const openNewApplication = () => {
  editingApplicationId.value = null;
  selectedApplication.value = null;
  showApplicationDialog.value = true;
};

const openEditApplication = (application) => {
  const raw = rawApplications.value.find(({ id }) => id === application.id);
  if (!raw) return;
  editingApplicationId.value = raw.id;
  selectedApplication.value = {
    name: raw.name,
    description: raw.description,
    allowedIps: raw.allowedIps,
    expiresAt: raw.expiresAt,
    requestLimit: raw.requestLimit,
    requestLimitIntervalMinutes: raw.requestLimitIntervalMinutes,
  };
  showApplicationDialog.value = true;
};

const openPermissions = (application) => router.push({ name: "security-api-application-permissions", params: { id: application.id } });

const updateApplicationDialogVisibility = (visible) => {
  showApplicationDialog.value = visible;
  if (!visible) {
    selectedApplication.value = null;
    editingApplicationId.value = null;
  }
};

const saveApplication = ({ application, isNew }) => {
  if (isNew) {
    ApiApplicationService.createApiApplication(application).then((res) => {
      if (!res) return;
      showApplicationDialog.value = false;
      generatedSecret.value = res.data.key;
      secretDialogTitle.value = "Aplicación creada con éxito";
      showSecretDialog.value = true;
      loadApplications();
    });
    return;
  }

  ApiApplicationService.updateApiApplication(editingApplicationId.value, application).then((res) => {
    if (!res) return;
    Notification.success("Aplicación actualizada correctamente.", 3000);
    showApplicationDialog.value = false;
    loadApplications();
  });
};

const rotateKey = (application) => {
  ElMessageBox.confirm(
    `La clave actual de "${application.name}" dejará de funcionar en cuanto se genere la nueva. ¿Continuar?`,
    "Rotar clave",
    { confirmButtonText: "Sí, rotar", cancelButtonText: "Cancelar", type: "warning" }
  ).then(() => {
    ApiApplicationService.rotateKey(application.id).then((res) => {
      if (!res) return;
      generatedSecret.value = res.data.key;
      secretDialogTitle.value = "Clave rotada correctamente";
      showSecretDialog.value = true;
    });
  }).catch(() => {});
};

const revokeApplication = (application) => {
  ElMessageBox.confirm(
    `Se revocará el acceso de "${application.name}" de forma permanente. La clave dejará de funcionar. ¿Continuar?`,
    "Revocar aplicación",
    { confirmButtonText: "Sí, revocar", cancelButtonText: "Cancelar", type: "warning" }
  ).then(() => {
    ApiApplicationService.revoke(application.id).then((res) => {
      if (!res) return;
      Notification.success("Aplicación revocada correctamente.", 3000);
      loadApplications();
    });
  }).catch(() => {});
};

onMounted(() => {
  loadApplications();
});
</script>

<template>
  <section class="api-applications">
    <HeaderTitle title="Seguridad" />

    <SecuritySectionTitle title="Aplicaciones API" />

    <el-row class="mt-2">
      <el-card class="card-search api-applications__filters w-100 wback" body-style="padding: 0">
        <div class="search-form">
          <div class="row">
            <div class="col-4 el-col-xs-24">
              <div class="form-item">
                <label for="api-application-search">Nombre o descripción</label>
                <el-input id="api-application-search" v-model="filters.search" class="w-100"
                  placeholder="Buscar por nombre o descripción" clearable @keyup.enter="search" />
              </div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="form-item">
                <label class="w-100">Estado</label>
                <el-select v-model="filters.status" class="w-100" placeholder="Todos" clearable @change="search">
                  <el-option label="Activa" value="active" />
                  <el-option label="Caducada" value="expired" />
                  <el-option label="Revocada" value="revoked" />
                </el-select>
              </div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="d-flex justify-content-center">
                <div class="col-md-6 align-self-center el-col-xs-12">
                  <button type="button" class="btn btn_red btn__new" @click="openNewApplication">
                    NUEVA
                  </button>
                </div>
                <div class="col-md-6 align-self-center el-col-xs-12">
                  <button type="button" class="btn btn_yellow btn__search" @click="search">
                    BUSCAR
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>

    <div class="table table-not-mobile api-applications__table">
      <table>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Prefijo</th>
            <th>Estado</th>
            <th>Límite</th>
            <th>Caduca</th>
            <th>Último uso</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="application in listedApplications" :key="application.id">
            <td>
              <div class="application-name">
                <span class="application-name__avatar" :style="{ backgroundColor: application.tone }">{{
                  application.initials }}</span>
                <span><b>{{ application.name }}</b><small>{{ application.description }}</small></span>
              </div>
            </td>
            <td>{{ application.prefix }}</td>
            <td>
              <span class="status" :class="application.status">{{ statusLabel[application.status] }}</span>
            </td>
            <td>{{ requestLimitLabel(application) }}</td>
            <td>{{ expiresLabel(application) }}</td>
            <td>{{ lastUsedLabel(application) }}</td>
            <td>
              <div class="row-actions">
                <el-tooltip content="Permisos y alcance" placement="top" effect="light">
                  <font-awesome-layers class="fa-2x" @click="openPermissions(application)">
                    <font-awesome-icon icon="circle" style="color: #474ce6" />
                    <font-awesome-icon icon="shield-alt" class="fa-inverse" transform="shrink-7" />
                  </font-awesome-layers>
                </el-tooltip>
                <el-tooltip content="Editar aplicación" placement="top" effect="light">
                  <font-awesome-layers class="fa-2x" @click="openEditApplication(application)">
                    <font-awesome-icon icon="circle" style="color: #f7d20e" />
                    <font-awesome-icon icon="pen" class="fa-inverse" transform="shrink-7" />
                  </font-awesome-layers>
                </el-tooltip>
                <el-tooltip v-if="application.status !== 'revoked'" content="Rotar clave" placement="top" effect="light">
                  <font-awesome-layers class="fa-2x" @click="rotateKey(application)">
                    <font-awesome-icon icon="circle" style="color: #18a99a" />
                    <font-awesome-icon icon="key" class="fa-inverse" transform="shrink-7" />
                  </font-awesome-layers>
                </el-tooltip>
                <el-tooltip v-if="application.status !== 'revoked'" content="Revocar aplicación" placement="top" effect="light">
                  <font-awesome-layers class="fa-2x" @click="revokeApplication(application)">
                    <font-awesome-icon icon="circle" style="color: #f9151c" />
                    <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                </el-tooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="!filteredApplications.length" class="empty">
        No se han encontrado aplicaciones con los filtros seleccionados.
      </p>
    </div>

    <div class="table-mobile api-applications__cards">
      <el-card v-for="application in listedApplications" :key="application.id" class="api-application-card">
        <div class="application-name">
          <span class="application-name__avatar" :style="{ backgroundColor: application.tone }">{{ application.initials
          }}</span>
          <span><b>{{ application.name }}</b><small>{{ application.description }}</small></span>
        </div>
        <p><b>Prefijo:</b>{{ application.prefix }}</p>
        <p>
          <b>Estado:</b><span class="status" :class="application.status">{{ statusLabel[application.status] }}</span>
        </p>
        <p><b>Límite:</b>{{ requestLimitLabel(application) }}</p>
        <p><b>Caduca:</b>{{ expiresLabel(application) }}</p>
        <p><b>Último uso:</b>{{ lastUsedLabel(application) }}</p>
        <div class="api-application-card__actions">
          <el-tooltip content="Permisos y alcance" placement="top" effect="light">
            <font-awesome-layers class="fa-2x" @click="openPermissions(application)">
              <font-awesome-icon icon="circle" style="color: #474ce6" /><font-awesome-icon icon="shield-alt"
                class="fa-inverse" transform="shrink-7" />
            </font-awesome-layers>
          </el-tooltip>
          <el-tooltip content="Editar aplicación" placement="top" effect="light">
            <font-awesome-layers class="fa-2x" @click="openEditApplication(application)">
              <font-awesome-icon icon="circle" style="color: #f7d20e" /><font-awesome-icon icon="pen" class="fa-inverse"
                transform="shrink-7" />
            </font-awesome-layers>
          </el-tooltip>
          <el-tooltip v-if="application.status !== 'revoked'" content="Rotar clave" placement="top" effect="light">
            <font-awesome-layers class="fa-2x" @click="rotateKey(application)">
              <font-awesome-icon icon="circle" style="color: #18a99a" /><font-awesome-icon icon="key" class="fa-inverse"
                transform="shrink-7" />
            </font-awesome-layers>
          </el-tooltip>
          <el-tooltip v-if="application.status !== 'revoked'" content="Revocar aplicación" placement="top" effect="light">
            <font-awesome-layers class="fa-2x" @click="revokeApplication(application)">
              <font-awesome-icon icon="circle" style="color: #f9151c" /><font-awesome-icon icon="times"
                class="fa-inverse" transform="shrink-6" />
            </font-awesome-layers>
          </el-tooltip>
        </div>
      </el-card>
      <p v-if="!filteredApplications.length" class="empty">
        No se han encontrado aplicaciones con los filtros seleccionados.
      </p>
    </div>

    <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage"
      :total="filteredApplications.length" :page-size-options="[10, 20, 50]" class="pagination"
      @size-change="page = 1" />

    <ApiApplicationDialog :model-value="showApplicationDialog" :application="selectedApplication"
      @update:model-value="updateApplicationDialogVisibility" @save="saveApplication" />
    <ApiApplicationSecretDialog v-model="showSecretDialog" :secret="generatedSecret" :title="secretDialogTitle" />
  </section>
</template>

<style lang="scss" scoped>
.api-applications {
  color: #30385d;
}

.security-subtitle {
  align-items: center;
  display: flex;
  margin-bottom: 10px;
}

.security-subtitle .svg-inline--fa {
  color: #474ce6;
  font-size: 27px;
}

.security-subtitle .title-text {
  color: #474ce6;
  font-size: 2.5em;
  margin-left: 1rem;
}

.api-applications__filters {
  background: #fff !important;
  background-image: none !important;
  border-radius: 15px;
}

.btn__search {
  color: #474ce6 !important;
  font-weight: 800;
  margin-left: 10px;
}

.btn__new {
  color: #fff !important;
  font-weight: 800;
}

.btn__search,
.btn__new {
  height: 3em;
  margin-top: 22px;
}

.api-applications__table table {
  min-width: 0;
  table-layout: fixed;
}

.api-applications__table th:nth-child(1),
.api-applications__table td:nth-child(1) {
  width: 25%;
}

.api-applications__table th:nth-child(2),
.api-applications__table td:nth-child(2) {
  width: 10%;
}

.api-applications__table th:nth-child(3),
.api-applications__table td:nth-child(3) {
  width: 10%;
}

.api-applications__table th:nth-child(4),
.api-applications__table td:nth-child(4) {
  width: 13%;
}

.api-applications__table th:nth-child(5),
.api-applications__table td:nth-child(5) {
  width: 12%;
}

.api-applications__table th:nth-child(6),
.api-applications__table td:nth-child(6) {
  width: 14%;
}

.api-applications__table th:nth-child(7),
.api-applications__table td:nth-child(7) {
  width: 16%;
}

.api-applications__table th {
  text-transform: uppercase;
}

.api-applications__table td {
  vertical-align: middle;
}

.application-name {
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
}

.application-name>span:last-child {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.application-name b,
.application-name small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.application-name b {
  font-size: 0.88rem;
}

.application-name small {
  color: #7c83a2;
  display: block;
  font-size: 0.72rem;
  font-weight: 400;
}

.application-name__avatar {
  align-items: center;
  border: 2px solid #474ce6;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  flex: 0 0 34px;
  font-size: 0.62rem;
  font-weight: 800;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.status {
  border: 1px solid;
  border-radius: 15px;
  font-size: 0.75rem;
  padding: 3px 10px;
}

.active {
  background: #effbf4;
  border-color: #c9eed9;
  color: #279653;
}

.expired {
  background: #fff7ef;
  border-color: #ffe0ba;
  color: #fb6b21;
}

.revoked {
  background: #fdeceb;
  border-color: #f6c7c3;
  color: #d0342c;
}

.row-actions,
.api-application-card__actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.row-actions :deep(.fa-layers),
.api-application-card__actions :deep(.fa-layers) {
  cursor: pointer;
}

.table-mobile {
  display: none;
}

.pagination {
  display: flex;
  justify-content: center;
  margin: 16px 0 22px;
}

.empty {
  color: #7c83a2;
  text-align: center;
}

@media (min-width: 1200px) {
  .table-mobile {
    display: none;
  }
}

@media (min-width: 768px) and (max-width: 1199px) {
  .api-applications__filters .col-4 {
    flex: 0 0 50%;
    max-width: 50%;
  }

  .api-applications__filters .col-4:last-child {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .api-applications__filters .d-flex {
    justify-content: flex-start !important;
  }

  .btn__search,
  .btn__new {
    width: 100%;
  }

  .btn__search {
    margin-left: 0;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1199px) {
  .api-applications__cards {
    display: grid;
    gap: 12px;
  }

  .api-applications__cards :deep(.el-card__body) {
    padding: 14px;
  }

  .api-application-card {
    position: relative;
  }

  .api-application-card p {
    display: grid;
    gap: 7px;
    grid-template-columns: 105px minmax(0, 1fr);
    margin: 10px 0 0;
    word-break: break-word;
  }

  .api-application-card p b {
    color: #474ce6;
  }

  .api-application-card__actions {
    justify-content: flex-end;
    margin-top: 14px;
  }
}

@media (max-width: 767px) {
  .table-not-mobile {
    display: none;
  }

  .security-subtitle .title-text {
    font-size: 2em;
  }

  .api-applications__filters {
    background: #fff !important;
  }

  .pagination {
    max-width: 100%;
    overflow: hidden;
  }
}
</style>
